/**
 * ProfileActions — action cluster that differs by own vs other.
 *
 * Own:   Edit Profile + Share Profile buttons.
 * Other: Follow + Message buttons + ··· menu (Mute / Copy link / Share / Report).
 *        No "Block" — TruXSocial is a no-block / mute-only platform by design.
 *        Every control here is live. Mute and Copy link were both decorative and
 *        said so in a toast; because mute-only is the whole safety model, the
 *        decorative one was the control a member reaches for first.
 *
 * 1.24.00 (E): the rebuilt ShareProfileDialog — real preview, working Copy link,
 *        conditional native share, six platform channels, the ≤280 note and the
 *        real server-rendered QR — could only be opened from your OWN profile,
 *        because "Share Profile" lived in the isOwn branch alone. Mobile gained
 *        exactly this entry point in the same slice (UserProfile's ··· opens
 *        ShareProfileSheet), so leaving web without it shipped the two clients
 *        inconsistent. The row reuses ProfileView's existing dialog state, which
 *        already threads the real isOwn — so on someone else's profile the
 *        dialog renders with isOwn={false} and the personal /join/<code> invite
 *        tile stays own-profile only.
 *
 * Uses an inner-component split (OtherActions) to keep hooks unconditional
 * per Rules of Hooks — identical pattern to AboutAuthorPanel.
 */
import { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Button, IconButton, Popover, useToast } from '@/ui';
import { Icon } from '@/icons';
import { useFollow, useMute, useRequireAuth, followButtonLabel } from '@/hooks';
import { ReportModal } from '@/components/ReportModal/ReportModal';
import { buildProfileUrl } from '@/core/lib/postUrl';
import { messagesService } from '@/core/services/messagesService';
import { ApiError } from '@/core/lib/apiClient';
import type { User } from '@/core/models';

// ── Props ──────────────────────────────────────────────────────────────────────

export interface ProfileActionsProps {
  user: User;
  isOwn: boolean;
  onEditProfile: () => void;
  onShareProfile: () => void;
}

// ── Styled ─────────────────────────────────────────────────────────────────────

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  text-align: left;
  transition: background 120ms ease;

  &:hover {
    background: ${({ theme }) => theme.color.glassBg};
  }
`;

const MenuItemDanger = styled(MenuItem)`
  color: ${({ theme }) => theme.color.accentMagenta};
`;

// ── Inner component for "other" profile — hooks are always called ──────────────

function OtherActions({
  user,
  onShareProfile,
}: {
  user: User;
  onShareProfile: () => void;
}) {
  const { isFollowing, isRequested, toggleFollow } = useFollow(user.handle);
  const { isMuted, toggleMute } = useMute(user.handle);
  const requireAuth = useRequireAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [reportOpen, setReportOpen] = useState(false);

  const [openingDm, setOpeningDm] = useState(false);

  function handleFollow() {
    if (!requireAuth('follow')) return;
    // A tap that silently does nothing reads as a broken button: say so when it fails.
    Promise.resolve(toggleFollow()).catch(() =>
      toast(`Couldn't update your follow of ${user.handle}. Try again.`),
    );
  }

  // Opens the conversation WITH this person. It used to navigate to /messages and stop,
  // leaving the member to find or start the thread themselves. createDm is idempotent:
  // an existing thread comes back as it is, a new one is created (as a message request
  // where this person's settings ask for that).
  async function handleMessage() {
    if (!requireAuth('message') || openingDm) return;
    setOpeningDm(true);
    try {
      const convo = await messagesService.createDm(user.handle);
      void navigate('/messages', { state: { openConversation: convo.id } });
    } catch (e) {
      toast(
        e instanceof ApiError && e.code === 'dm_not_allowed'
          ? `${user.name} isn't accepting messages from you.`
          : "Couldn't open that conversation. Try again.",
      );
    } finally {
      setOpeningDm(false);
    }
  }

  // Mute was decorative here — it toasted "Muted — this is decorative for now."
  // and created no row — while the identical action from a post's ··· menu wrote a
  // real Mute. Mute is this platform's whole answer to "make this person go away",
  // so the surface a member reaches for first was the one that did nothing. Same
  // path as PostMoreMenu: the muted set is already hydrated at boot, so this needs
  // no new API. The label reads Unmute when muted — without that there was no way
  // to unmute from a profile at all, because muting removes that author's posts
  // and the post menu was the only other place offering it.
  function handleMute() {
    if (!requireAuth('mute this account')) return;
    void toggleMute().then((muted) => {
      if (muted === null) {
        toast('Something went wrong');
        return;
      }
      toast(muted ? `Muted ${user.handle}` : `Unmuted ${user.handle}`);
    });
  }

  // Also previously decorative, and worse than a no-op: it toasted "Link copied"
  // while touching no clipboard, so the member pasted whatever they had copied
  // before. Same implementation as ShareProfileDialog's working Copy link —
  // and since 1.24.00 E1 the same buildProfileUrl helper, so neither surface
  // can hand out an unencoded handle the other would have escaped.
  async function handleCopyLink() {
    const url = buildProfileUrl(user.handle);
    try {
      await navigator.clipboard.writeText(url);
      toast('Profile link copied');
    } catch {
      toast('Could not copy link');
    }
  }

  return (
    <Row>
      <Button
        variant={isFollowing || isRequested ? 'secondary' : 'outline'}
        onClick={handleFollow}
        aria-label={`${followButtonLabel(isFollowing, isRequested)} ${user.handle}`}
      >
        {followButtonLabel(isFollowing, isRequested)}
      </Button>

      <Button variant="secondary" onClick={() => void handleMessage()} disabled={openingDm}>
        <Icon name="mail" size={16} />
        Message
      </Button>

      <Popover
        trigger={
          <IconButton icon="more-horizontal" label="More" />
        }
      >
        <MenuItem type="button" role="menuitem" onClick={handleMute}>
          <Icon name="shield" size={16} />
          {isMuted ? `Unmute ${user.handle}` : `Mute ${user.handle}`}
        </MenuItem>
        {/* Copy link and Share profile both stay, and they are not the same
            control: this one puts the URL on the clipboard in a single click,
            the next one opens a chooser (platforms, a note, the QR, the OS
            sheet). Mobile's ··· menu carries the same pair for the same
            reason, so the two clients read alike. */}
        <MenuItem type="button" role="menuitem" onClick={() => void handleCopyLink()}>
          <Icon name="link" size={16} />
          Copy link to profile
        </MenuItem>
        <MenuItem type="button" role="menuitem" onClick={onShareProfile}>
          <Icon name="share" size={16} />
          Share profile
        </MenuItem>
        <MenuItemDanger
          type="button"
          role="menuitem"
          onClick={() => {
            if (requireAuth('report this account')) setReportOpen(true);
          }}
        >
          <Icon name="flag" size={16} />
          Report account
        </MenuItemDanger>
      </Popover>

      {reportOpen && (
        <ReportModal
          target={{ kind: 'account', user }}
          onClose={() => setReportOpen(false)}
        />
      )}
    </Row>
  );
}

// ── Public component ────────────────────────────────────────────────────────────

export function ProfileActions({
  user,
  isOwn,
  onEditProfile,
  onShareProfile,
}: ProfileActionsProps) {
  if (isOwn) {
    return (
      <Row>
        <Button variant="secondary" onClick={onEditProfile}>
          Edit Profile
        </Button>
        <Button variant="primary" onClick={onShareProfile}>
          Share Profile
        </Button>
      </Row>
    );
  }

  return <OtherActions user={user} onShareProfile={onShareProfile} />;
}
